import { useState, useEffect, useMemo } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Sparkles, RefreshCw, Check } from "lucide-react";
import { toast } from "sonner";

const ALL_MEALS = [
  { key: "breakfast", label: "Frokost" },
  { key: "lunch", label: "Lunsj" },
  { key: "dinner", label: "Middag" },
  { key: "dessert", label: "Dessert" },
];
const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
const DAY_LABEL = { monday: "Man", tuesday: "Tir", wednesday: "Ons", thursday: "Tor", friday: "Fre", saturday: "Lør", sunday: "Søn" };

/**
 * Dialog for generating the shopping list from the current week's meal-plan.
 * Lets the user pick which meal-types to include and which specific recipes
 * (uncheck any recipe they don't want this time).
 *
 * Props:
 *   - open, onOpenChange (std)
 *   - weekStart (ISO date string for the week)
 *   - shift (current active shift)
 *   - servings (optional — scale recipes to this many people; falls back to boat default)
 *   - onGenerated(shoppingList) — parent uses it to refresh state
 */
export default function GenerateFromPlanDialog({ open, onOpenChange, weekStart, shift, servings, onGenerated }) {
  const [mealTypes, setMealTypes] = useState(new Set(["breakfast", "lunch", "dinner", "dessert"]));
  const [includeBaking, setIncludeBaking] = useState(true);
  const [plannedRecipes, setPlannedRecipes] = useState([]); // [{id,title,meal_type,days:[]}]
  const [disabledRecipeIds, setDisabledRecipeIds] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [scaleServings, setScaleServings] = useState(servings || 0);

  // Sync local scaleServings whenever the dialog opens or the prop changes
  useEffect(() => {
    if (open) setScaleServings(servings || 0);
  }, [open, servings]);

  useEffect(() => {
    if (!open || !weekStart) return;
    (async () => {
      setLoading(true);
      setDisabledRecipeIds(new Set());
      try {
        const [planRes, bakingRes, recipesRes] = await Promise.allSettled([
          api.getMealPlan(weekStart, shift || "1"),
          api.getBakingPlan(weekStart),
          api.getRecipes(false),
        ]);
        const plan = planRes.status === "fulfilled" ? planRes.value.data : {};
        const baking = bakingRes.status === "fulfilled" ? bakingRes.value.data : { items: [] };
        const recipes = recipesRes.status === "fulfilled" ? recipesRes.value.data : [];
        const recipeMap = Object.fromEntries(recipes.map((r) => [r.id, r]));

        // Aggregate recipe occurrences across days × meals
        const found = new Map(); // id → {id, title, meal_types:Set, days:Set}
        for (const day of DAYS) {
          const dayData = plan?.[day] || {};
          for (const meal of ["breakfast", "lunch", "dinner", "dessert"]) {
            const slot = dayData?.[meal] || {};
            const rid = slot.recipe_id;
            if (!rid) continue;
            const existing = found.get(rid) || { id: rid, title: recipeMap[rid]?.title || slot.name || "(Oppskrift slettet)", meal_types: new Set(), days: new Set(), image: recipeMap[rid]?.image_url || recipeMap[rid]?.has_image };
            existing.meal_types.add(meal);
            existing.days.add(day);
            found.set(rid, existing);
          }
        }
        // Baking items
        for (const item of baking?.items || []) {
          if (!item?.recipe_id) continue;
          const existing = found.get(item.recipe_id) || { id: item.recipe_id, title: recipeMap[item.recipe_id]?.title || item.name || "(Bakst)", meal_types: new Set(["baking"]), days: new Set(), image: recipeMap[item.recipe_id]?.image_url };
          existing.meal_types.add("baking");
          if (item.day) existing.days.add(item.day);
          found.set(item.recipe_id, existing);
        }
        setPlannedRecipes([...found.values()].sort((a, b) => a.title.localeCompare(b.title, "nb")));
      } catch {
        toast.error("Kunne ikke laste ukeplanen");
      } finally { setLoading(false); }
    })();
  }, [open, weekStart, shift]);

  const toggleMealType = (key) => {
    setMealTypes((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const toggleRecipe = (id) => {
    setDisabledRecipeIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Filter the list by selected meal types so we only show relevant recipes
  const visibleRecipes = useMemo(() => {
    return plannedRecipes.filter((r) => {
      const hasMealMatch = [...r.meal_types].some((m) => mealTypes.has(m) || (m === "baking" && includeBaking));
      return hasMealMatch;
    });
  }, [plannedRecipes, mealTypes, includeBaking]);

  const selectedRecipeIds = useMemo(
    () => visibleRecipes.filter((r) => !disabledRecipeIds.has(r.id)).map((r) => r.id),
    [visibleRecipes, disabledRecipeIds]
  );

  const generate = async () => {
    if (selectedRecipeIds.length === 0) {
      toast.error("Velg minst én oppskrift");
      return;
    }
    setGenerating(true);
    try {
      const res = await api.generateShoppingList(weekStart, {
        recipe_ids: selectedRecipeIds,
        meal_types: [...mealTypes],
        shift: shift || "1",
        include_baking: includeBaking,
        servings: scaleServings > 0 ? scaleServings : undefined,
      });
      toast.success(`Handleliste generert fra ${selectedRecipeIds.length} oppskrift${selectedRecipeIds.length === 1 ? "" : "er"}`);
      onGenerated?.(res.data);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Feil ved generering");
    } finally { setGenerating(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="generate-from-plan-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            <Sparkles className="h-5 w-5 text-navy-800" /> Generer fra ukeplan
          </DialogTitle>
          <DialogDescription>
            Velg hvilke måltidstyper og oppskrifter som skal tas med i handlelista.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Meal types */}
          <div>
            <label className="text-xs font-semibold text-stone-500 uppercase tracking-wide">Måltidstyper</label>
            <div className="flex flex-wrap gap-2 mt-2">
              {ALL_MEALS.map((m) => (
                <button
                  key={m.key}
                  onClick={() => toggleMealType(m.key)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    mealTypes.has(m.key)
                      ? "bg-navy-800 text-white"
                      : "bg-stone-100 text-stone-600 hover:bg-stone-200"
                  }`}
                  data-testid={`gen-meal-${m.key}`}
                >
                  {mealTypes.has(m.key) && <Check className="h-3.5 w-3.5 inline mr-1" />}
                  {m.label}
                </button>
              ))}
              <button
                onClick={() => setIncludeBaking((v) => !v)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  includeBaking ? "bg-amber-600 text-white" : "bg-stone-100 text-stone-600 hover:bg-stone-200"
                }`}
                data-testid="gen-meal-baking"
              >
                {includeBaking && <Check className="h-3.5 w-3.5 inline mr-1" />}
                Bakst
              </button>
            </div>
          </div>

          {/* Portion scaling */}
          <div className="flex items-center gap-3 rounded-md border border-stone-200 bg-stone-50 px-3 py-2">
            <label htmlFor="gen-servings" className="text-xs font-semibold text-stone-600 uppercase tracking-wide whitespace-nowrap">
              Skaler til
            </label>
            <input
              id="gen-servings"
              type="number"
              min="0"
              step="1"
              value={scaleServings || ""}
              onChange={(e) => setScaleServings(parseInt(e.target.value) || 0)}
              placeholder="auto"
              className="w-16 rounded-md border border-stone-200 bg-white px-2 py-1 text-sm text-center focus:border-navy-800 focus:ring-1 focus:ring-navy-800 outline-none"
              data-testid="gen-servings-input"
            />
            <span className="text-xs text-stone-500">personer</span>
            <span className="text-[11px] text-stone-400 ml-auto">
              {scaleServings > 0 ? "Mengder skaleres fra oppskriftens basisporsjon." : "Tomt = bruk båtens/skiftets standard."}
            </span>
          </div>

          {/* Recipes */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold text-stone-500 uppercase tracking-wide">
                Oppskrifter i uken ({visibleRecipes.length})
              </label>
              {visibleRecipes.length > 0 && (
                <div className="flex gap-2">
                  <button
                    onClick={() => setDisabledRecipeIds(new Set())}
                    className="text-xs text-sky-700 hover:underline"
                    data-testid="gen-select-all"
                  >Velg alle</button>
                  <span className="text-stone-300">·</span>
                  <button
                    onClick={() => setDisabledRecipeIds(new Set(visibleRecipes.map((r) => r.id)))}
                    className="text-xs text-stone-500 hover:underline"
                    data-testid="gen-select-none"
                  >Fjern alle</button>
                </div>
              )}
            </div>
            {loading ? (
              <p className="text-sm text-stone-400 text-center py-6">Laster ukeplanen…</p>
            ) : visibleRecipes.length === 0 ? (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-center text-sm text-amber-800" data-testid="gen-no-recipes">
                Ingen oppskrifter er lagt til i ukeplanen for valgte måltidstyper. Legg til oppskrifter i «Ukeplan» først, eller prøv et annet måltid.
              </div>
            ) : (
              <ul className="divide-y divide-stone-100 max-h-80 overflow-y-auto border border-stone-200 rounded-md">
                {visibleRecipes.map((r) => {
                  const active = !disabledRecipeIds.has(r.id);
                  return (
                    <li
                      key={r.id}
                      className={`flex items-center gap-3 px-3 py-2 transition-colors cursor-pointer ${active ? "bg-white hover:bg-stone-50" : "bg-stone-50 opacity-70"}`}
                      onClick={() => toggleRecipe(r.id)}
                      data-testid={`gen-recipe-${r.id}`}
                    >
                      <Checkbox checked={active} onCheckedChange={() => toggleRecipe(r.id)} />
                      <div className="flex-1 min-w-0">
                        <div className={`text-sm font-medium truncate ${active ? "text-stone-800" : "text-stone-500 line-through"}`}>
                          {r.title}
                        </div>
                        <div className="text-[11px] text-stone-400 flex items-center gap-2">
                          <span>{[...r.meal_types].map((m) => ({ breakfast: "Frokost", lunch: "Lunsj", dinner: "Middag", dessert: "Dessert", baking: "Bakst" })[m] || m).join(", ")}</span>
                          <span>·</span>
                          <span>{[...r.days].map((d) => DAY_LABEL[d] || d).join(", ")}</span>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
            {visibleRecipes.length > 0 && (
              <p className="text-xs text-stone-500 mt-2" data-testid="gen-summary">
                <strong>{selectedRecipeIds.length}</strong> av {visibleRecipes.length} oppskrifter valgt
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-2">
            <button
              onClick={() => onOpenChange(false)}
              className="flex-1 rounded-md border border-stone-200 bg-white px-4 py-2.5 text-sm font-medium text-stone-700 hover:bg-stone-50 transition-colors"
              data-testid="gen-cancel"
              disabled={generating}
            >
              Avbryt
            </button>
            <button
              onClick={generate}
              disabled={generating || loading || selectedRecipeIds.length === 0}
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-md bg-navy-800 px-4 py-2.5 text-sm font-semibold text-white hover:bg-navy-900 transition-colors disabled:opacity-40"
              data-testid="gen-confirm"
            >
              {generating ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              {generating ? "Genererer…" : `Generer (${selectedRecipeIds.length})`}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
